'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { Icon } from '../Icon'
import { useReplaceQuery } from '@/util/router'
import { useSearchParams } from 'next/navigation'
import { useState, useMemo, Fragment } from 'react'
import { ClickOutsideDetector } from '../ClickOutsideDetector'
import { FilterTab, FilterTabItem } from './FilterTab'
import { WithT } from '@/locales/types'
import { createT } from '@/locales/index'
import { ChartFilterSheet } from './ChartFilterSheet'
import { ComplexFilter } from './ComplexFIlter'
import { IFilter } from '@/api/interfaces/IFilter'
import { ChannelType } from '@/api/types/channel'

type SearchFilter = { text: string; id: string }

type SearchOrder = FilterTabItem

type Props = {
  useSearchParam?: boolean
  sheet?: boolean
  wheres?: SearchFilter[]
  orders?: SearchOrder[]
  filters?: IFilter.IFilterRO
  disableOrder?: boolean
  disableWhereAll?: boolean
  filterOverride?: () => React.ReactNode
}

export const ListFilter = ({
  useSearchParam,
  sheet,
  wheres,
  orders,
  filters,
  disableOrder,
  translations,
  disableWhereAll,
  filterOverride,
}: WithT<Props>) => {
  const tabId = 'search-order'
  const menuId = 'search-where'

  const t = createT(translations)

  const computedOrders = useMemo(() => {
    if (orders?.length) {
      return orders
    }

    return [
      { text: t('order-popular'), id: 'POPULAR' },
      { text: t('order-recent'), id: 'RECENT' },
    ]
  }, [orders, t])

  const _filters = useMemo(() => {
    if (disableWhereAll) {
      return wheres || []
    }

    return [{ text: t('all'), id: '' }, ...(wheres || [])]
  }, [wheres, t, disableWhereAll])

  const searchParams = useSearchParams()
  const { replaceQuery } = useReplaceQuery()

  const [filterMenu, toggleFilterMenu] = useState(false)
  const [_tab, _setTab] = useState(computedOrders[0].id)
  const [_menu, _setMenu] = useState(_filters[0].id)

  const tab = useMemo(() => {
    if (useSearchParam) {
      return searchParams.get(tabId) || computedOrders[0].id
    }

    return _tab
  }, [useSearchParam, searchParams, _tab, computedOrders])

  const menu = useMemo(() => {
    if (useSearchParam) {
      return searchParams.get(menuId) || _filters[0].id
    }

    return _menu
  }, [useSearchParam, searchParams, _menu, _filters])

  const onChangeTab = (val: string) => {
    if (useSearchParam) {
      replaceQuery(tabId, val)
    } else {
      _setTab(val)
    }
  }

  const onChangeMenu = (val: string) => {
    if (useSearchParam) {
      replaceQuery(menuId, val)
    } else {
      _setMenu(_menu)
    }
    closeFilterMenu()
  }

  const closeFilterMenu = () => {
    toggleFilterMenu(false)
  }

  const menuComponent = (
    <div className="relative z-10 w-24">
      <div className="flex gap-3 w-full">
        <button
          className="border-gray-400 rounded-lg px-3 py-2 border flex gap-1 items-center w-full justify-between"
          onClick={() => toggleFilterMenu(true)}
        >
          <span className="text-body2 font-medium leading-tight">
            {_filters.find((f) => f.id === menu)?.text}
          </span>
          <Icon name="caret-down" size={14} />
        </button>
      </div>
      <AnimatePresence>
        {filterMenu && (
          <ClickOutsideDetector onClickOutside={closeFilterMenu}>
            <motion.ul
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute left-0 top-0 w-full border border-gray-400 rounded-lg bg-gray-0 py-1"
            >
              {_filters.map((f, i) => {
                return (
                  <Fragment key={f.id}>
                    <li
                      className="px-3 py-2 flex gap-1 items-center cursor-pointer"
                      onClick={() => onChangeMenu(f.id)}
                    >
                      <span className="text-body2 font-medium leading-tight">
                        {f.text}
                      </span>
                    </li>
                    {i < _filters.length - 1 && (
                      <hr className="mx-3 border-gray-200" />
                    )}
                  </Fragment>
                )
              })}
            </motion.ul>
          </ClickOutsideDetector>
        )}
      </AnimatePresence>
    </div>
  )

  return (
    <div className="py-3 flex items-center justify-between gap-3">
      {filterOverride ? (
        filterOverride()
      ) : (
        <>
          {sheet && (
            <ComplexFilter id="list-filter" translations={translations} />
          )}
          {!sheet && wheres && menuComponent}
        </>
      )}

      {!disableOrder && (
        <FilterTab
          tabs={computedOrders}
          value={tab}
          onChange={onChangeTab}
          id={tabId}
        />
      )}

      {sheet && (
        <ChartFilterSheet
          id="list-filter"
          channelType={ChannelType.YOUTUBE}
          filters={filters}
          translations={translations}
        />
      )}
    </div>
  )
}
